(() => {
  if (window.__ssBundleInit) return;
  window.__ssBundleInit = true;

  const REINIT_COOLDOWN_MS = 800;
  let lastReinitAt = 0;

  const getBlockInitKey = (blockEl) => {
    if (!blockEl) return '';
    return [
      blockEl.dataset.productId || '',
      blockEl.dataset.currentGroup || '',
      blockEl.dataset.pairCoilSize || ''
    ].join('|');
  };

  const FIXED_AIRCON_PRODUCTS = [
    'rechargeable-battery-powered-portable-pressure-car-washer-15l',
    'mizu-foam-air-conditioner-cleaner-500ml',
    'air-conditioner-wall-bracket-450mm-medium-duty-max-150kg',
    'wall-bracket-550mm'
  ];

  const AIRCON_ACCESSORY_COLLECTIONS = [
    'air-conditioner-accessories'
  ];

  const AIRCON_SIZE_RULES = {
    '1/4 3/8': {
      pairCoilHandle: '3m-air-conditioner-pair-coil-tube-1-4-3-8-insulated-copper-pipes-r32-r410a',
      installationKitHandle: 'air-conditioning-first-floor-installation-kit-1'
    },
    '1/4 1/2': {
      pairCoilHandle: 'ac-pair-coil-with-flare-nuts-1-4-1-2-3m',
      installationKitHandle: 'air-conditioning-first-floor-installation-kit-bundle-1-41-2'
    },
    '1/4 5/8': {
      pairCoilHandle: 'ac-pair-coil-with-flare-nuts-1-4-5-8-3m',
      installationKitHandle: 'air-conditioning-first-floor-installation-kit-bundle-1-45-8'
    }
  };

  const formatMoney = (cents) => {
    const n = Number(cents || 0);
    return '$' + (n / 100).toFixed(2);
  };

  const safeImage = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.src || value.url || '';
    return '';
  };

  const clampQty = (value) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) || n < 0 ? 0 : n;
  };

  const normalizeText = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[–—-]/g, ' ')
      .replace(/[_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizeSizeKey = (value) => {
    const text = normalizeText(value).replace(/\s+/g, ' ').trim();
    const digits = text.match(/\d+\s*\/\s*\d+/g);
    if (!digits || digits.length < 2) return text;

    return `${digits[0].replace(/\s+/g, '')} ${digits[1].replace(/\s+/g, '')}`;
  };

  const createEl = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (typeof text === 'string') el.textContent = text;
    return el;
  };

  const isAirconType = (product) => {
    const text = normalizeText(
      `${product.product_type || ''} ${product.title || ''} ${product.handle || ''}`
    );

    return (
      text.includes('air conditioner') ||
      text.includes('air conditioners') ||
      text.includes('aircon') ||
      text.includes('window box') ||
      text.includes('ducted') ||
      text.includes('split system')
    );
  };

  const isAccessoryType = (product) => {
    const text = normalizeText(
      `${product.product_type || ''} ${product.title || ''} ${product.handle || ''} ${product.sku || ''}`
    );

    return (
      text.includes('accessor') ||
      text.includes('bracket') ||
      text.includes('support') ||
      text.includes('capacitor') ||
      text.includes('isolator') ||
      text.includes('pad') ||
      text.includes('drain') ||
      text.includes('cover') ||
      text.includes('pair coil') ||
      text.includes('installation kit') ||
      text.includes('install kit') ||
      text.includes('pipe kit') ||
      text.includes('interconnect') ||
      text.includes('copper pair coil')
    );
  };

  const isValidProduct = (product) => {
    const text = normalizeText(product.title || '');
    return !text.includes('bulk');
  };

  const getProductFamilyKey = (product) => {
    const raw = normalizeText(
      `${product.title || ''} ${product.handle || ''} ${product.product_type || ''}`
    );

    return raw
      .replace(/\b\d+\s*\/\s*\d+\b/g, ' ')
      .replace(/\b\d+(?:\.\d+)?\s*mm\b/g, ' ')
      .replace(/\b\d+(?:\.\d+)?\s*m\b/g, ' ')
      .replace(/\b\d+(?:\.\d+)?\s*(kg|kw|l|ml)\b/g, ' ')
      .replace(/\b\d+\b/g, ' ')
      .replace(/\b(max|medium duty|heavy duty|duty|rated)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const dedupeById = (products) => {
    const seen = new Set();

    return products.filter((product) => {
      const key = String(product.id || product.variant_id || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const dedupeByFamily = (products) => {
    const seen = new Set();

    return products.filter((product) => {
      const familyKey = getProductFamilyKey(product);
      if (!familyKey) return true;
      if (seen.has(familyKey)) return false;
      seen.add(familyKey);
      return true;
    });
  };

  const dedupeProductsSmart = (products) => {
    return dedupeByFamily(dedupeById(products));
  };

  const normalizeProduct = (product, root) => {
    const variant = Array.isArray(product.variants)
      ? (product.variants.find((v) => v.available) || product.variants[0])
      : null;

    if (!variant) return null;

    return {
      id: Number(product.id),
      variant_id: Number(variant.id),
      title: product.title,
      sku: variant.sku || '',
      price: Number(variant.price || 0),
      compare_at_price: Number(variant.compare_at_price || 0),
      image:
        product.featured_image ||
        (Array.isArray(product.images) ? product.images[0] : '') ||
        product.image ||
        '',
      url: product.url || (product.handle ? `${root}products/${product.handle}` : '#'),
      product_type: product.type || product.product_type || '',
      handle: product.handle || '',
      collections: Array.isArray(product.collections)
        ? product.collections
        : []
    };
  };

  const getSectionRoot = (el) =>
    el?.closest('.shopify-section, section[id^="shopify-section-"], [id^="shopify-section-"]');

  const findBundleBlockForForm = (form) => {
    const sectionRoot = getSectionRoot(form);
    if (!sectionRoot) return null;

    const blocks = Array.from(sectionRoot.querySelectorAll('[id^="ss-bundle-block-"]'));
    if (!blocks.length) return null;

    const visibleBlock = blocks.find((block) => {
      return block.offsetParent !== null && block.dataset.bundleReady === 'true';
    });

    return visibleBlock || blocks[0] || null;
  };

  const refreshCartUI = async (root = '/') => {
    try {
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart.js');
      }

      const cart = await response.json();
      const itemCount = Number(cart.item_count || 0);

      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = itemCount;
      });

      document.querySelectorAll('.cart-count-bubble, .header__cart-count, .cart-count').forEach((el) => {
        el.textContent = itemCount;
      });

      return cart;
    } catch (error) {
      console.error('refreshCartUI error:', error);
      return null;
    }
  };

  const refreshHorizonCartUI = async (root = '/') => {
    try {
      const currentItems = document.querySelector('cart-items-component[data-section-id]');
      const headerSectionId = currentItems?.getAttribute('data-section-id');

      if (!headerSectionId) {
        throw new Error('Cannot find current cart header section id');
      }

      const response = await fetch(
        `${root}?sections=${encodeURIComponent(headerSectionId)},cart-icon-bubble,cart-live-region-text`,
        {
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cart sections');
      }

      const sections = await response.json();
      const parser = new DOMParser();

      if (typeof sections['cart-icon-bubble'] === 'string' && sections['cart-icon-bubble'].trim()) {
        const bubbleDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble =
          bubbleDoc.getElementById('shopify-section-cart-icon-bubble') ||
          bubbleDoc.querySelector('#shopify-section-cart-icon-bubble');
        const currentBubble =
          document.getElementById('shopify-section-cart-icon-bubble') ||
          document.querySelector('#shopify-section-cart-icon-bubble');

        if (newBubble && currentBubble) {
          currentBubble.replaceWith(newBubble);
        }
      }

      if (typeof sections['cart-live-region-text'] === 'string' && sections['cart-live-region-text'].trim()) {
        const liveDoc = parser.parseFromString(sections['cart-live-region-text'], 'text/html');
        const newLive =
          liveDoc.getElementById('shopify-section-cart-live-region-text') ||
          liveDoc.querySelector('#shopify-section-cart-live-region-text');
        const currentLive =
          document.getElementById('shopify-section-cart-live-region-text') ||
          document.querySelector('#shopify-section-cart-live-region-text');

        if (newLive && currentLive) {
          currentLive.replaceWith(newLive);
        }
      }

      const headerHtml = sections[headerSectionId];
      if (typeof headerHtml === 'string' && headerHtml.trim()) {
        const headerDoc = parser.parseFromString(headerHtml, 'text/html');

        const newDrawerComponent =
          headerDoc.querySelector('cart-drawer-component') ||
          headerDoc.querySelector('.cart-drawer');

        const currentDrawerComponent =
          document.querySelector('cart-drawer-component') ||
          document.querySelector('.cart-drawer');

        if (newDrawerComponent && currentDrawerComponent) {
          currentDrawerComponent.replaceWith(newDrawerComponent);
          return true;
        }

        const newDialog = headerDoc.querySelector('dialog.cart-drawer__dialog');
        const currentDialog = document.querySelector('dialog.cart-drawer__dialog');

        if (newDialog && currentDialog) {
          currentDialog.replaceWith(newDialog);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('refreshHorizonCartUI error:', error);
      return false;
    }
  };

  const notifyCartUpdated = (cart) => {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: { cart, source: 'ss-bundle' }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart, source: 'ss-bundle' }
        })
      );

      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { cart, source: 'ss-bundle' }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:change', {
          detail: { cart, source: 'ss-bundle' }
        })
      );
    } catch (error) {
      console.error('notifyCartUpdated error:', error);
    }
  };

  const setSubmitButtonLoading = (form, loading) => {
    const submitButton =
      form.querySelector('[type="submit"]') ||
      form.querySelector('button[name="add"]') ||
      form.querySelector('button[type="button"][data-add-to-cart]');

    if (!submitButton) return;

    if (!submitButton.dataset.originalText) {
      submitButton.dataset.originalText = submitButton.textContent || 'Add to cart';
    }

    submitButton.disabled = loading;
    submitButton.textContent = loading
      ? 'Adding...'
      : (submitButton.dataset.originalText || 'Add to cart');
  };

  const markFormProcessing = (form, value) => {
    if (!form) return;
    form.dataset.ssBundleProcessing = value ? 'true' : 'false';
  };

  const isFormProcessing = (form) => {
    return form?.dataset?.ssBundleProcessing === 'true';
  };

  const initBundleBlock = (blockEl) => {
    if (!blockEl) return;

    const nextInitKey = getBlockInitKey(blockEl);
    const currentInitKey = blockEl.dataset.bundleInitKey || '';

    if (
      blockEl.dataset.bundleReady === 'true' &&
      currentInitKey === nextInitKey
    ) {
      return;
    }

    blockEl.dataset.bundleReady = 'true';
    blockEl.dataset.bundleInitKey = nextInitKey;

    const productId = blockEl.dataset.productId;
    const currentGroup = blockEl.dataset.currentGroup || 'general';
    const tradeCollectionHandle = blockEl.dataset.tradeCollectionHandle || '';
    const isTradeCustomer = blockEl.dataset.isTradeCustomer === 'true';
    const tradeProductHandlesScript = blockEl.querySelector('[data-trade-product-handles]');
    const tradeProductHandles = tradeProductHandlesScript
      ? JSON.parse(tradeProductHandlesScript.textContent || '[]')
      : [];
    const rootRaw = (blockEl.dataset.root || '').trim();
    const root = rootRaw ? (rootRaw.endsWith('/') ? rootRaw : `${rootRaw}/`) : '/';
    const limit = Number(blockEl.dataset.maxProducts || 6);
    const metafieldPairCoilSize = (blockEl.dataset.pairCoilSize || '').trim();

    const listEl = blockEl.querySelector('[data-list]');
    const loadingEl = blockEl.querySelector('[data-loading]');
    const emptyEl = blockEl.querySelector('[data-empty]');
    const totalEl = blockEl.querySelector('[data-total]');
    const footerEl = blockEl.querySelector('[data-footer]');
    const saveEl = blockEl.querySelector('[data-save]');
    const messageEl = blockEl.querySelector('[data-message]');

    if (!productId || !listEl || !loadingEl || !emptyEl || !totalEl || !footerEl) return;

    let selected = [];
    let isProcessing = false;

    const belongsToGroup = (product) => {
      if (currentGroup === 'aircon') {
        return isAccessoryType(product) || !isAirconType(product);
      }

      if (currentGroup === 'electrical') {
        const text = normalizeText(
          `${product.product_type || ''} ${product.title || ''} ${product.handle || ''}`
        );

        return (
          text.includes('electrical') ||
          text.includes('switch') ||
          text.includes('light') ||
          text.includes('lighting')
        );
      }

      return true;
    };

    const updateTotal = () => {
      let total = 0;
      let originalTotal = 0;

      selected.forEach((item) => {
        const price = Number(item.price || 0);
        const compareAtPrice = Number(item.compare_at_price || 0);
        const quantity = Number(item.quantity || 0);

        total += price * quantity;

        if (compareAtPrice > price) {
          originalTotal += compareAtPrice * quantity;
        } else {
          originalTotal += price * quantity;
        }
      });

      const saving = Math.max(0, originalTotal - total);

      totalEl.textContent = formatMoney(total);
      footerEl.hidden = selected.length === 0;

      if (saveEl) {
        const totalBox = footerEl.querySelector('.ss-bundle__total-box');

        if (saving > 0) {
          saveEl.hidden = false;
          saveEl.textContent = `You save ${formatMoney(saving)}`;
          totalBox?.classList.add('has-saving');
        } else {
          saveEl.hidden = true;
          saveEl.textContent = '';
          totalBox?.classList.remove('has-saving');
        }
      }
    };

    const rebuildSelectedFromDOM = () => {
      const nextSelected = [];

      listEl.querySelectorAll('.ss-card').forEach((card) => {
        const variantId = Number(card.dataset.variantId);
        const price = Number(card.dataset.price || 0);
        const compareAtPrice = Number(card.dataset.compareAtPrice || 0);
        const input = card.querySelector('.ss-qty__input');
        const qty = clampQty(input?.value || 0);

        if (variantId && qty > 0) {
          nextSelected.push({
            id: variantId,
            quantity: qty,
            price,
            compare_at_price: compareAtPrice
          });
          card.classList.add('is-selected');
        } else {
          card.classList.remove('is-selected');
        }
      });

      selected = nextSelected;
      updateTotal();
    };

    const getBundleItemsFromDOM = () => {
      const bundleMap = new Map();

      listEl.querySelectorAll('.ss-card').forEach((card) => {
        const input = card.querySelector('.ss-qty__input');
        const variantId = Number(card.dataset.variantId);
        const qty = clampQty(input?.value || 0);

        if (!variantId || qty <= 0) return;

        bundleMap.set(variantId, qty);
      });

      return Array.from(bundleMap.entries()).map(([id, quantity]) => ({
        id,
        quantity
      }));
    };

    const getMainVariantId = (form) => {
      const variantInput = form?.querySelector('[name="id"]');
      return variantInput ? Number(variantInput.value) : null;
    };

    const getMainQty = (form) => {
      const qtyInput = form?.querySelector('[name="quantity"]');
      return qtyInput ? Math.max(1, clampQty(qtyInput.value || 1)) : 1;
    };

    const fetchProductByHandle = async (handle) => {
      try {
        const response = await fetch(`${root}products/${handle}.js`, {
          credentials: 'same-origin'
        });

        if (!response.ok) return null;

        const data = await response.json();
        return normalizeProduct(data, root);
      } catch (_) {
        return null;
      }
    };

    const fetchCollectionProducts = async (handle) => {
      try {
        const response = await fetch(
          `${root}collections/${handle}/products.json?limit=50`,
          { credentials: 'same-origin' }
        );

        if (!response.ok) return [];

        const data = await response.json();
        const products = Array.isArray(data?.products) ? data.products : [];

        return products
          .map((product) => normalizeProduct(product, root))
          .filter(Boolean)
          .filter(isValidProduct)
          .filter((product) => String(product.id) !== String(productId));
      } catch (_) {
        return [];
      }
    };

    const fetchFixedAirconProductsGlobal = async () => {
      if (currentGroup !== 'aircon') return [];

      const results = [];

      for (const handle of FIXED_AIRCON_PRODUCTS) {
        const item = await fetchProductByHandle(handle);
        if (item && String(item.id) !== String(productId)) {
          results.push(item);
        }
      }

      return dedupeProductsSmart(results).filter(isValidProduct);
    };

    const fetchMappedSizeProducts = async () => {
      if (currentGroup !== 'aircon') return [];

      const selectedSizeRaw = metafieldPairCoilSize;
      const selectedSize = normalizeSizeKey(selectedSizeRaw);

      if (!selectedSize || !AIRCON_SIZE_RULES[selectedSize]) {
        return [];
      }

      const rule = AIRCON_SIZE_RULES[selectedSize];
      const results = [];

      if (rule.pairCoilHandle) {
        const pairCoil = await fetchProductByHandle(rule.pairCoilHandle);
        if (pairCoil && String(pairCoil.id) !== String(productId)) {
          results.push({
            ...pairCoil,
            badgeText: 'Matched size',
            badgeClass: 'ss-card__badge ss-card__badge--matched'
          });
        }
      }

      if (rule.installationKitHandle) {
        const installationKit = await fetchProductByHandle(rule.installationKitHandle);
        if (installationKit && String(installationKit.id) !== String(productId)) {
          results.push({
            ...installationKit,
            badgeText: 'Matched size',
            badgeClass: 'ss-card__badge ss-card__badge--matched'
          });
        }
      }

      return dedupeProductsSmart(results).filter(isValidProduct);
    };

    const fetchRecommendations = async () => {
      try {
        const response = await fetch(
          `${root}recommendations/products.json?product_id=${productId}&limit=${Math.max(limit * 2, 12)}`,
          { credentials: 'same-origin' }
        );

        if (!response.ok) return [];

        const data = await response.json();
        const products = Array.isArray(data?.products) ? data.products : [];

        return dedupeProductsSmart(
          products
            .map((product) => normalizeProduct(product, root))
            .filter(Boolean)
            .filter(isValidProduct)
            .filter((product) => String(product.id) !== String(productId))
            .filter((product) => belongsToGroup(product))
        );
      } catch (_) {
        return [];
      }
    };

    const fetchFallbackProducts = async () => {
      if (currentGroup === 'aircon') {
        let merged = [];
        for (const handle of AIRCON_ACCESSORY_COLLECTIONS) {
          const items = await fetchCollectionProducts(handle);
          merged = merged.concat(items);
        }
        return dedupeProductsSmart(merged)
          .filter(isValidProduct)
          .filter((product) => belongsToGroup(product));
      }

      if (currentGroup === 'electrical') {
        let merged = [];
        for (const handle of ['electrical-tools', 'lighting']) {
          const items = await fetchCollectionProducts(handle);
          merged = merged.concat(items);
        }
        return dedupeProductsSmart(merged)
          .filter(isValidProduct)
          .filter((product) => belongsToGroup(product));
      }

      return [];
    };

    const createCard = (product) => {
      const imageUrl = safeImage(product.image);
      const hasImage = !!imageUrl;

      const article = createEl('article', 'ss-card');
      article.dataset.variantId = String(Number(product.variant_id));
      article.dataset.price = String(Number(product.price || 0));
      article.dataset.compareAtPrice = String(Number(product.compare_at_price || 0));

      const currentPrice = Number(product.price || 0);
      const originalPrice = Number(product.compare_at_price || 0);
      const hasDiscount = originalPrice > currentPrice;
      const hasTradePrice =
        isTradeCustomer &&
        tradeProductHandles.includes(product.handle);

      const media = createEl('div', 'ss-card__media');
      const mediaBadges = createEl('div', 'ss-card__media-badges');

      if (product.badgeText && product.badgeClass) {
        mediaBadges.appendChild(
          createEl('span', product.badgeClass, product.badgeText)
        );

      } else if (hasTradePrice) {

        mediaBadges.appendChild(
          createEl(
            'span',
            'ss-card__badge ss-card__badge--trade',
            'Trade price'
          )
        );

      } else if (hasDiscount) {

        mediaBadges.appendChild(
          createEl(
            'span',
            'ss-card__badge ss-card__badge--sale',
            'On sale'
          )
        );

      } else {

        mediaBadges.appendChild(
          createEl(
            'span',
            'ss-card__badge ss-card__badge--recommended',
            'Recommended'
          )
        );
      }

      media.appendChild(mediaBadges);

      if (hasImage) {
        const img = document.createElement('img');
        img.className = 'ss-card__img';
        img.src = imageUrl;
        img.alt = product.title || '';
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        media.appendChild(createEl('div', 'ss-card__placeholder'));
      }

      const body = createEl('div', 'ss-card__body');

      const titleLink = createEl('a', 'ss-card__title', product.title || '');
      titleLink.href = product.url || '#';
      titleLink.title = product.title || '';

      const priceWrap = createEl('div', 'ss-card__price-wrap price price--on-sale');

      if (hasDiscount) {
        const originalPriceEl = createEl(
          's',
          'ss-card__price-original price-item price-item--regular',
          formatMoney(originalPrice)
        );

        const currentPriceEl = createEl(
          'span',
          'ss-card__price price-item price-item--sale',
          formatMoney(currentPrice)
        );

        priceWrap.appendChild(originalPriceEl);
        priceWrap.appendChild(currentPriceEl);
      } else {
        priceWrap.classList.remove('price--on-sale');

        const regularPriceEl = createEl(
          'span',
          'ss-card__price ss-card__price--regular price-item price-item--regular',
          formatMoney(currentPrice)
        );

        priceWrap.appendChild(regularPriceEl);
      }

      const actions = createEl('div', 'ss-card__actions');
      const qtyWrap = createEl('div', 'ss-qty');

      const minus = createEl('button', 'ss-qty__btn', '−');
      minus.type = 'button';

      const input = document.createElement('input');
      input.className = 'ss-qty__input';
      input.type = 'number';
      input.min = '0';
      input.step = '1';
      input.value = '0';
      input.dataset.variantId = String(Number(product.variant_id));
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('aria-label', `Quantity for ${product.title || 'product'}`);

      const plus = createEl('button', 'ss-qty__btn', '+');
      plus.type = 'button';

      qtyWrap.appendChild(minus);
      qtyWrap.appendChild(input);
      qtyWrap.appendChild(plus);
      actions.appendChild(qtyWrap);

      body.appendChild(titleLink);
      body.appendChild(priceWrap);
      body.appendChild(actions);

      article.appendChild(media);
      article.appendChild(body);

      const syncSelected = () => {
        input.value = String(clampQty(input.value));
        rebuildSelectedFromDOM();
      };

      minus.addEventListener('click', () => {
        input.value = String(Math.max(0, clampQty(input.value) - 1));
        syncSelected();
      });

      plus.addEventListener('click', () => {
        input.value = String(clampQty(input.value) + 1);
        syncSelected();
      });

      input.addEventListener('input', syncSelected);
      input.addEventListener('change', syncSelected);
      input.addEventListener('blur', syncSelected);

      return article;
    };

    const render = (products) => {
      listEl.innerHTML = '';
      const fragment = document.createDocumentFragment();

      products.forEach((product) => {
        fragment.appendChild(createCard(product));
      });

      listEl.appendChild(fragment);
      listEl.hidden = false;
      footerEl.hidden = true;
      rebuildSelectedFromDOM();
    };

    const resetBundleSelections = () => {
      const cards = listEl.querySelectorAll('.ss-card');

      cards.forEach((card) => {
        card.classList.remove('is-selected');

        const qtyInput = card.querySelector('.ss-qty__input');
        if (qtyInput) {
          qtyInput.value = '0';
        }
      });

      rebuildSelectedFromDOM();
    };

    blockEl.__ssBundleApi = {
      get root() {
        return root;
      },
      get messageEl() {
        return messageEl;
      },
      get isProcessing() {
        return isProcessing;
      },
      set isProcessing(value) {
        isProcessing = value;
      },
      rebuildSelectedFromDOM,
      getBundleItemsFromDOM,
      getMainVariantId,
      getMainQty,
      resetBundleSelections
    };

    const init = async () => {
      try {
        let products = [];

        if (currentGroup === 'aircon') {
          const fixed = await fetchFixedAirconProductsGlobal();
          const mappedSizeProducts = await fetchMappedSizeProducts();
          const recommendations = await fetchRecommendations();
          const fallback = await fetchFallbackProducts();

          const others = dedupeProductsSmart([
            ...mappedSizeProducts,
            ...recommendations,
            ...fallback
          ]).filter((product) => belongsToGroup(product));

          products = dedupeProductsSmart([
            ...fixed,
            ...others
          ]).slice(0, limit);
        } else {
          const recommendations = await fetchRecommendations();
          const fallback = await fetchFallbackProducts();

          products = dedupeProductsSmart([
            ...recommendations,
            ...fallback
          ]).slice(0, limit);
        }

        loadingEl.hidden = true;

        if (!products.length) {
          emptyEl.hidden = false;
          listEl.hidden = true;
          footerEl.hidden = true;
        } else {
          emptyEl.hidden = true;
          render(products);
        }
      } catch (error) {
        console.error('ss-bundle init error:', error);
        loadingEl.hidden = true;
        emptyEl.hidden = false;
        listEl.hidden = true;
        footerEl.hidden = true;
      }
    };

    init();
  };

  const addBundleToCart = async (form, blockEl) => {
    if (!form || !blockEl || !blockEl.__ssBundleApi) return;

    const api = blockEl.__ssBundleApi;

    if (api.isProcessing || isFormProcessing(form)) return;

    api.rebuildSelectedFromDOM();

    const mainVariantId = api.getMainVariantId(form);
    if (!mainVariantId) return;

    const bundleItems = api.getBundleItemsFromDOM();

    if (!bundleItems.length) {
      const variantId = form.querySelector('[name="id"]')?.value;
      const qty = form.querySelector('[name="quantity"]')?.value || 1;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: Number(qty)
        })
      });

      return;
    }

    const mainQty = Number(api.getMainQty(form)) || 1;

    const items = [
      {
        id: Number(mainVariantId),
        quantity: mainQty
      },
      ...bundleItems
        .map((item) => ({
          id: Number(item.id),
          quantity: Number(item.quantity) || 0
        }))
        .filter((item) => item.id && item.quantity > 0)
    ];

    if (api.messageEl) api.messageEl.textContent = '';

    api.isProcessing = true;
    markFormProcessing(form, true);
    setSubmitButtonLoading(form, true);

    try {
      const response = await fetch(`${api.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        let errorMessage = 'Add to cart failed';

        try {
          const errorData = await response.json();
          errorMessage = errorData?.description || errorData?.message || errorMessage;

          if (
            typeof errorMessage === 'string' &&
            errorMessage.includes('maximum quantity')
          ) {
            errorMessage = '你已經加入了此商品可購買的最大數量。';
          }
        } catch (_) {}

        throw new Error(errorMessage);
      }

      await response.json();

      api.resetBundleSelections();

      const cart = await refreshCartUI(api.root);
      await refreshHorizonCartUI(api.root);
      notifyCartUpdated(cart);

      if (api.messageEl) {
        api.messageEl.textContent =
          'Main product and selected recommended products have been added to your cart.';
      }

      setTimeout(() => {
        if (api.messageEl) api.messageEl.textContent = '';
      }, 3000);
    } catch (error) {
      console.error('bundle add error:', error);

      if (api.messageEl) {
        api.messageEl.textContent =
          error?.message || 'Something went wrong. Please try again.';
      }
    } finally {
      api.isProcessing = false;
      markFormProcessing(form, false);
      setSubmitButtonLoading(form, false);
    }
  };

  const handleBundleButtonClick = (event) => {
    const button = event.target.closest(
      'button[type="submit"], button[name="add"], [data-add-to-cart]'
    );
    if (!button) return;

    const form = button.closest('form[action*="/cart/add"]');
    if (!form) return;

    const blockEl = findBundleBlockForForm(form);
    if (!blockEl || !blockEl.__ssBundleApi) return;

    const api = blockEl.__ssBundleApi;
    api.rebuildSelectedFromDOM();

    const bundleItems = api.getBundleItemsFromDOM();

    if (!bundleItems.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    addBundleToCart(form, blockEl);
  };

  const handleBundleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[action*="/cart/add"]')) return;

    const blockEl = findBundleBlockForForm(form);
    if (!blockEl || !blockEl.__ssBundleApi) return;

    const api = blockEl.__ssBundleApi;
    api.rebuildSelectedFromDOM();

    const bundleItems = api.getBundleItemsFromDOM();

    if (!bundleItems.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    addBundleToCart(form, blockEl);
  };

  const resetAndReinitAllBundleBlocks = () => {
    const now = Date.now();

    if (now - lastReinitAt < REINIT_COOLDOWN_MS) {
      return;
    }

    lastReinitAt = now;

    document.querySelectorAll('[id^="ss-bundle-block-"]').forEach((blockEl) => {
      const nextInitKey = getBlockInitKey(blockEl);
      const currentInitKey = blockEl.dataset.bundleInitKey || '';

      if (
        blockEl.dataset.bundleReady === 'true' &&
        currentInitKey === nextInitKey
      ) {
        return;
      }

      blockEl.dataset.bundleReady = 'false';
      blockEl.dataset.bundleInitKey = '';
      blockEl.__ssBundleApi = null;

      const listEl = blockEl.querySelector('[data-list]');
      const loadingEl = blockEl.querySelector('[data-loading]');
      const emptyEl = blockEl.querySelector('[data-empty]');
      const footerEl = blockEl.querySelector('[data-footer]');
      const totalEl = blockEl.querySelector('[data-total]');
      const messageEl = blockEl.querySelector('[data-message]');
      const saveEl = blockEl.querySelector('[data-save]');

      if (listEl) {
        listEl.innerHTML = '';
        listEl.hidden = true;
      }

      if (loadingEl) loadingEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      if (footerEl) footerEl.hidden = true;
      if (totalEl) totalEl.textContent = '$0.00';
      if (messageEl) messageEl.textContent = '';

      if (saveEl) {
        saveEl.hidden = true;
        saveEl.textContent = '';
      }

      const totalBox = blockEl.querySelector('.ss-bundle__total-box');
      if (totalBox) {
        totalBox.classList.remove('has-saving');
      }

      initBundleBlock(blockEl);
    });
  };

  const initAllBundleBlocks = () => {
    document.querySelectorAll('[id^="ss-bundle-block-"]').forEach(initBundleBlock);
  };

  if (!window.__ssBundleSubmitBound) {
    window.__ssBundleSubmitBound = true;
    document.addEventListener('click', handleBundleButtonClick, true);
    document.addEventListener('submit', handleBundleSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllBundleBlocks);
  } else {
    initAllBundleBlocks();
  }

  document.addEventListener('shopify:section:load', initAllBundleBlocks);

  document.addEventListener('shopify:block:select', (event) => {
    const blockEl = event.target?.closest?.('[id^="ss-bundle-block-"]');
    if (blockEl) {
      initBundleBlock(blockEl);
    }
  });

  document.addEventListener('ss:variant-changed', resetAndReinitAllBundleBlocks);
})();